import { useState } from "react";
import { Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useOperationalDirectory } from "@/hooks/queries/useOperationalDirectory";
import { getApiErrorCode } from "@/lib/api-error";

// 2026-10-02 (regression fix) — same code-to-message pattern used elsewhere
// (e.g. useConvertCreditToEpargne.ts) rather than showing the raw error code.
const ERROR_MESSAGES: Record<string, string> = {
  ATTRIBUTION_REASSIGNMENT_DISABLED:
    "La réattribution est désactivée. Un super-admin doit l'activer depuis Maintenance.",
  RESPONSIBLE_AGENT_NOT_FOUND: "Employé introuvable.",
  RESPONSIBLE_AGENT_INACTIVE: "Cet employé est inactif.",
  RESPONSIBLE_AGENT_INVALID_ROLE: "Ce rôle ne peut pas être employé responsable.",
  PROFORMA_ALREADY_PROMOTED_REASSIGN_INVOICE:
    "Ce devis a déjà été transformé en facture. Corrigez l'attribution sur la facture pour que les statistiques employé soient mises à jour.",
};

interface ReassignResponsibleAgentDialogProps {
  currentResponsibleAgentId: number;
  onReassign: (newResponsibleAgentId: number, reason?: string) => Promise<unknown>;
  triggerLabel?: string;
  triggerSize?: "sm" | "icon";
}

// 2026-10-02 — correction tool UI for proformas/invoices/commissions saved
// before attribution existed, or under the wrong employee. admin+ only
// (callers are responsible for that gate — see each server route). Never
// shown as "the normal way to create a transaction"; that's
// ResponsibleAgentSelect, at creation time.
export function ReassignResponsibleAgentDialog({
  currentResponsibleAgentId,
  onReassign,
  triggerLabel = "Réattribuer",
  triggerSize = "sm",
}: ReassignResponsibleAgentDialogProps) {
  const [open, setOpen] = useState(false);
  const [newAgentId, setNewAgentId] = useState<number | undefined>(undefined);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { data: directory = [] } = useOperationalDirectory();

  const current = directory.find((d) => d.id === currentResponsibleAgentId);

  async function submit() {
    if (!newAgentId) return;
    setSubmitting(true);
    try {
      await onReassign(newAgentId, reason.trim() || undefined);
      toast.success("Attribution corrigée.");
      setOpen(false);
      setNewAgentId(undefined);
      setReason("");
    } catch (err: unknown) {
      const code = getApiErrorCode(err);
      toast.error((code && ERROR_MESSAGES[code]) ?? "Erreur lors de la réattribution.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        {triggerSize === "icon" ? (
          <Button variant="ghost" size="icon" title="Réattribuer">
            <UserCog size={13} />
          </Button>
        ) : (
          <Button size="sm" variant="outline">
            <UserCog size={13} /> {triggerLabel}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Corriger l'employé responsable</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[12px]">
          <p className="text-muted-foreground">
            Cette correction modifie les statistiques employé, mais ne change pas l'utilisateur
            qui a saisi l'opération. L'action sera enregistrée dans le journal d'audit.
          </p>
          <p className="text-muted-foreground">
            Actuellement attribué à :{" "}
            <span className="font-medium text-body">{current?.fullName ?? `#${currentResponsibleAgentId}`}</span>
          </p>
          <div>
            <Label>Nouvel employé responsable</Label>
            <Select
              value={newAgentId ? String(newAgentId) : undefined}
              onValueChange={(v) => setNewAgentId(Number(v))}
            >
              <SelectTrigger>
                <SelectValue placeholder="— Sélectionner —" />
              </SelectTrigger>
              <SelectContent>
                {directory
                  .filter((d) => d.id !== currentResponsibleAgentId)
                  .map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>
                      {d.fullName}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Motif (optionnel)</Label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="flex w-full rounded border border-border bg-card px-3 py-2 text-sm resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button onClick={submit} disabled={!newAgentId || submitting}>
            {submitting ? <Loader2 size={13} className="animate-spin" /> : "Corriger"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
